import { createContext, useContext, useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { queryKeys } from '../hooks/queries/queryKeys'

// Define the interface for the context
interface AuthContextType {
   authStatus: number
   setAuthStatus: (status: number) => void
   isAuthLoading: boolean
}

// Create the context
const AuthContext = createContext<AuthContextType | undefined>(undefined)

// Custom hook to use the auth context
export function useAuthContext() {
   const context = useContext(AuthContext)
   if (!context) {
      throw new Error(
         'useAuthContext must be used within an AuthContextProvider',
      )
   }
   return context
}

// Provider component
export function AuthContextProvider({
   children,
}: {
   children: React.ReactNode
}) {
   const host_url = process.env.REACT_APP_HOST_URL
   const queryClient = useQueryClient()

   // Local state for auth status
   const [authStatus, setAuthStatus] = useState(400)

   // Auth Status Query
   const {
      isLoading: isAuthLoading,
      data: authData,
      refetch: refetchAuth,
   } = useQuery({
      queryKey: queryKeys.authStatus,
      queryFn: async () => {
         const response = await fetch(`${host_url}/api/authorized`)
         return response.status
      },
      enabled: !!host_url, // Only run if API URL is available
      staleTime: 60 * 60 * 1000, // 1 hour or until the page is refreshed
      refetchOnWindowFocus: false, // Disable aggressive refetching
      refetchOnReconnect: false, // Disable refetch on reconnect
      retry: 1,
   })

   useEffect(() => {
      if (authData !== undefined) {
         setAuthStatus(authData)
      }
   }, [authData])

   const values = {
      authStatus,
      setAuthStatus,
      isAuthLoading,
   }

   return <AuthContext.Provider value={values}>{children}</AuthContext.Provider>
}
